import React from 'react';
import Link from 'next/link';
import {
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Kbd,
  Link as ChakraLink,
  Select,
  Text,
  useColorMode,
  useDisclosure
} from '@chakra-ui/react';
import { MenuIcon, SearchIcon } from '@heroicons/react/solid';
import { MoonIcon, SunIcon } from '@heroicons/react/outline';
import { ClerkDocsLogo } from '@components';
import { SIDENAV_WIDTH } from './sidenav';
import { useDetectOS } from '@hooks';
import { URLS } from '@lib';
import { MobileMenu } from './mobile-menu';
import { Application, DocsNavigation, Instance } from '@types';
import { SignedIn, SignedOut } from '@clerk/nextjs';
import { AppAndInstanceSwitcherContext } from '@context';
import { useSegment } from '@hooks/use-segment';
import { useRouter } from 'next/router';
import { NavigationConfiguration } from '@lib/docs/types';
import { SearchDialog } from '../search/search-dialog';

export const DOCS_HEADER_HEIGHT = {
  base: '70px'
};

type Props = {
  navigation?: Array<DocsNavigation>;
  mdxNav?: NavigationConfiguration;
};

declare global {
  interface Window {
    ko: string;
  }
}

const instancesOrder = ['development', 'production'];

export function DocsHeader({ navigation, mdxNav }: Props): JSX.Element {
  const analytics = useSegment();
  const router = useRouter();
  const { isMacOS } = useDetectOS();
  const { colorMode, toggleColorMode } = useColorMode();
  const [showSearch, showSearchOverlay] = React.useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    apps,
    currentApp,
    setCurrentApp,
    currentInstance,
    setCurrentInstance
  } = React.useContext(AppAndInstanceSwitcherContext);

  const showKeysSelector = apps && currentApp && currentInstance;

  React.useEffect(() => {
    const handleSearchShortcut = (event: KeyboardEvent): void => {
      const { key, ctrlKey, metaKey } = event;
      const isKPressed = key === 'k';
      const isModifierPressed = isMacOS ? metaKey : ctrlKey;

      if (isKPressed && isModifierPressed) {
        event.preventDefault(); // Prevents focus going to address bar on Windows
        showSearchOverlay(cur => !cur);
      }
    };

    document.addEventListener('keydown', handleSearchShortcut);

    return () => {
      document.removeEventListener('keydown', handleSearchShortcut);
    };
  }, [isMacOS]);

  const clerkDomain = process.env.NEXT_PUBLIC_CLERK_DOMAIN || 'clerk.com';

  return (
    <>
      <SearchDialog isOpen={showSearch} setIsOpen={showSearchOverlay} />

      <Flex
        as="header"
        pos="fixed"
        borderBottom="1px solid"
        borderColor="var(--borderColor)"
        bg="var(--bodyBg)"
        h={{ base: DOCS_HEADER_HEIGHT.base }}
        w={{ base: 'full', lg: `calc(100vw - ${SIDENAV_WIDTH})` }}
        top="0"
        left={{ base: 0, lg: SIDENAV_WIDTH }}
        px={6}
        zIndex={999}
      >
        {/* Must be inside `header` with `position: fixed` in order to cover the whole screen  */}
        <MobileMenu
          isOpen={isOpen}
          onClose={onClose}
          navigation={navigation}
          mdxNav={mdxNav}
        />

        {/* Mobile & Tablet */}
        <Flex
          display={{ base: 'inline-flex', lg: 'none' }}
          align="center"
          justify="space-between"
          w="full"
        >
          <ClerkDocsLogo />

          <IconButton
            size="xs"
            color="gray.400"
            aria-label="Open menu"
            variant="ghost"
            onClick={onOpen}
            icon={<MenuIcon height="100%" />}
            boxSize={6}
            _hover={{ bgColor: 'unset', color: 'gray.500' }}
            _active={{ bgColor: 'unset' }}
          />
        </Flex>

        {/* Desktop */}
        <Flex
          display={{ base: 'none', lg: 'flex' }}
          justify="space-between"
          align="center"
          w="full"
        >
          <Flex gap={4}>
            <IconButton
              aria-label="theme selection"
              variant="outline"
              borderColor="var(--borderColor)"
              onClick={toggleColorMode}
              role="group"
              _hover={{
                bg: 'var(--highlightBg)'
              }}
              minHeight="40px"
            >
              <Icon
                as={colorMode === 'light' ? MoonIcon : SunIcon}
                boxSize={5}
                color="var(--iconColor)"
                transition="color 0.2s"
                _groupHover={{
                  color: 'var(--secondaryBlue)'
                }}
              />
            </IconButton>

            <IconButton
              aria-label="search"
              variant="outline"
              borderColor="var(--borderColor)"
              display={{ base: showKeysSelector ? 'flex' : 'none', xl: 'none' }}
              boxSize={10}
              onClick={() => showSearchOverlay(true)}
              role="group"
              _hover={{
                bg: 'var(--highlightBg)'
              }}
              minHeight="40px"
            >
              <Icon
                as={SearchIcon}
                boxSize={5}
                color="var(--iconColor)"
                transition="color 0.2s"
                _groupHover={{
                  color: 'var(--secondaryBlue)'
                }}
              />
            </IconButton>

            <InputGroup
              w={56}
              h={10}
              display={{ base: showKeysSelector ? 'none' : 'flex', xl: 'flex' }}
              onClick={() => showSearchOverlay(true)}
              cursor="pointer"
            >
              <InputLeftElement pointerEvents="none" h="inherit">
                <Icon as={SearchIcon} boxSize="4" color="gray.400" />
              </InputLeftElement>

              <Input
                placeholder="Search docs"
                variant="outline"
                h="inherit"
                pr={20}
                pointerEvents="none"
                _placeholder={{
                  color: 'var(--secondaryTextColor)'
                }}
              />

              <InputRightElement pointerEvents="none" px={10} h="inherit">
                <Kbd
                  color="var(--secondaryTextColor)"
                  borderColor="var(--secondaryTextColor)"
                >
                  {isMacOS ? '⌘' : 'Ctrl'}
                </Kbd>{' '}
                <Text
                  as="span"
                  color="var(--secondaryTextColor)"
                  fontWeight="medium"
                  px={0.5}
                  pos="relative"
                  top="-0.5"
                >
                  +
                </Text>{' '}
                <Kbd
                  color="var(--secondaryTextColor)"
                  borderColor="var(--secondaryTextColor)"
                >
                  K
                </Kbd>
              </InputRightElement>
            </InputGroup>

            {showKeysSelector && (
              <>
                <Select
                  color="var(--primaryTextColor)"
                  value={currentApp.application_name}
                  onChange={e => {
                    const app = apps.find(
                      ({ application_name }) =>
                        application_name === e.target.value
                    );
                    setCurrentApp(app as Application);
                  }}
                  rootProps={{
                    w: '120px',
                    sx: {
                      '@media(min-width: 1100px)': {
                        w: '170px'
                      }
                    }
                  }}
                >
                  {apps.map(({ application_id, application_name }) => (
                    <option key={application_id} value={application_name}>
                      {application_name}
                    </option>
                  ))}
                </Select>

                <Select
                  color="var(--primaryTextColor)"
                  value={currentInstance.environment_type}
                  onChange={e => {
                    const currentInstance = currentApp.instances.find(
                      ({ environment_type }) =>
                        environment_type === e.target.value
                    );
                    setCurrentInstance(currentInstance as Instance);
                  }}
                  rootProps={{
                    w: '120px',
                    sx: {
                      '@media(min-width: 1100px)': {
                        w: '170px'
                      }
                    }
                  }}
                >
                  {currentApp.instances
                    .sort((a, b) => {
                      return (
                        instancesOrder.indexOf(a['environment_type']) -
                        instancesOrder.indexOf(b['environment_type'])
                      );
                    })
                    .map(({ instance_id, environment_type }) => (
                      <option key={instance_id} value={environment_type}>
                        {environment_type.charAt(0).toUpperCase() +
                          environment_type.slice(1)}
                      </option>
                    ))}
                </Select>
              </>
            )}
          </Flex>

          <Flex align="center" gap={4} marginRight={4}>
            <Text
              as="a"
              textStyle="sm-medium"
              color="var(--secondaryBlue)"
              onClick={() => {
                analytics.track(`Docs_Header_Go to Clerk.com Button Clicked`, {
                  surface: 'Docs',
                  location: 'Header',
                  page: router.pathname
                });
              }}
              href={URLS.home}
            >
              Go to {clerkDomain}
            </Text>

            <SignedOut>
              <Link
                legacyBehavior
                href={process.env.NEXT_PUBLIC_DASHBOARD_URL + '/sign-up'}
                passHref
              >
                <Button
                  as={ChakraLink}
                  variant="blue"
                  isExternal
                  ml={4}
                  border="1px"
                  borderColor="transparent"
                  className="hover:bg-blue-700"
                  onClick={() => {
                    analytics.track(`Docs_Header_Sign up Clicked`, {
                      surface: 'Docs',
                      location: 'Header',
                      page: router.pathname
                    });
                  }}
                >
                  Sign up
                </Button>
              </Link>
            </SignedOut>
            <SignedIn>
              <Link
                legacyBehavior
                href={process.env.NEXT_PUBLIC_DASHBOARD_URL as string}
                passHref
              >
                <Button
                  as={ChakraLink}
                  variant="blue"
                  isExternal
                  ml={4}
                  border="1px"
                  borderColor="transparent"
                  className="hover:bg-blue-700"
                  onClick={() => {
                    analytics.track(`Docs_Header_Dashboard Clicked`, {
                      surface: 'Docs',
                      location: 'Header',
                      page: router.pathname
                    });
                  }}
                >
                  Dashboard
                </Button>
              </Link>
            </SignedIn>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}
